import { LogoMarquee } from '@/components/landing/logo-marquee'
import { WhitepaperLeadModal } from '@/components/landing/whitepaper-lead-modal'
import { MarketingNav } from '@/components/layout/marketing-nav'
import { SiteFooter } from '@/components/layout/site-footer'
import { Button } from '@/components/ui/button'
import {
  LANDING_LEAD_MODAL_SESSION_KEYS,
  useLandingLeadModalTrigger,
} from '@/hooks/use-landing-lead-modal-trigger'
import { ChevronDown } from 'lucide-react'
import { useEffect, useLayoutEffect, useState } from 'react'
import type React from 'react'
import { Link } from 'react-router-dom'

const useCaseTabs = ['Offboarding', 'Upskilling', 'Scaling Operations', 'Mental Health'] as const

const useCaseContent = [
  {
    category: 'Offboarding',
    title: 'Compassionate Transitions',
    description:
      'Guide departing employees through career transitions with dignity and support. Our platform provides personalized pathways, career coaching, and job placement assistance to ensure smooth transitions that protect both your employer brand and employee well-being.',
    imageSrc: '/assets/insights-image.png',
  },
  {
    category: 'Upskilling',
    title: 'Future-Ready Workforce',
    description:
      'Empower your current employees with targeted skill development programs aligned to market demands. Our platform identifies skill gaps, creates personalized learning paths, and connects employees with opportunities that match their career aspirations and organizational needs.',
    imageSrc: '/assets/insights-image.png',
  },
  {
    category: 'Scaling Operations',
    title: 'Agile Growth Solutions',
    description:
      'Scale your operations seamlessly while supporting your workforce through periods of rapid change. Our solutions help organizations manage workforce transitions, optimize team structures, and maintain productivity during scaling initiatives with comprehensive employee support.',
    imageSrc: '/assets/insights-image.png',
  },
  {
    category: 'Mental Health',
    title: 'Holistic Employee Support',
    description:
      'Prioritize employee mental health and well-being during organizational changes. Our platform provides access to mental health resources, stress management tools, and supportive communities that help employees navigate uncertainty and maintain resilience throughout their career journey.',
    imageSrc: '/assets/insights-image.png',
  },
]

export function LandingPage() {
  // State management
  const [activeUseCase, setActiveUseCase] = useState(0)
  const [isScrolled, setIsScrolled] = useState(false)
  const [openFaqItems, setOpenFaqItems] = useState<Set<string>>(new Set())
  const [leadModalOpen, setLeadModalOpen] = useState(false)

  useLayoutEffect(() => {
    if (!import.meta.env.DEV) return
    const params = new URLSearchParams(window.location.search)
    if (params.get('resetLeadModal') !== '1') return
    try {
      sessionStorage.removeItem(LANDING_LEAD_MODAL_SESSION_KEYS.dismissed)
      sessionStorage.removeItem(LANDING_LEAD_MODAL_SESSION_KEYS.submitted)
    } catch {
      // ignore
    }
    params.delete('resetLeadModal')
    const next = params.toString()
    const path = `${window.location.pathname}${next ? `?${next}` : ''}${window.location.hash}`
    window.history.replaceState(null, '', path)
  }, [])

  useLandingLeadModalTrigger(() => setLeadModalOpen(true))

  /**
   * Smooth scroll to section with header offset
   */

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault()
    const element = document.getElementById(sectionId)
    if (element) {
      // Account for fixed header
      const headerOffset = 80
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })
    }
  }

  /**
   * Video autoplay handler - ensures videos play automatically on all devices
   * Handles mobile browser restrictions and enforces muted state
   */
  useEffect(() => {
    const videos = document.querySelectorAll('video')
    const cleanupFunctions: (() => void)[] = []

    for (const video of videos) {
      const videoElement = video as HTMLVideoElement
      // Ensure all autoplay attributes are set for mobile compatibility
      videoElement.muted = true
      videoElement.volume = 0
      videoElement.loop = true
      videoElement.playsInline = true
      videoElement.setAttribute('playsinline', 'true')
      videoElement.setAttribute('webkit-playsinline', 'true')
      videoElement.setAttribute('x5-playsinline', 'true') // For Android WeChat/QQ browsers
      videoElement.setAttribute('muted', 'true')
      videoElement.preload = 'auto'
      videoElement.defaultMuted = true

      // Hide native controls to prevent play button overlay
      videoElement.controls = false
      videoElement.removeAttribute('controls')

      // Apply inline styles to hide any play button
      videoElement.style.pointerEvents = 'none'
      videoElement.style.outline = 'none'

      // Ensure video stays muted and volume is 0
      const enforceMuted = () => {
        videoElement.muted = true
        videoElement.volume = 0
        videoElement.setAttribute('muted', 'true')
      }

      // Continuously enforce muted state (in case it gets changed)
      const mutedInterval = setInterval(enforceMuted, 100)

      // Also enforce on volume change events
      videoElement.addEventListener('volumechange', enforceMuted)

      // Function to attempt playing the video
      const attemptPlay = async () => {
        try {
          // Force muted state before attempting to play
          enforceMuted()

          // Hide any native controls
          videoElement.controls = false
          videoElement.removeAttribute('controls')

          await videoElement.play()

          // Ensure muted after play starts
          enforceMuted()

          // If successful, hide play button overlays with inline styles
          videoElement.style.pointerEvents = 'none'
          videoElement.style.outline = 'none'
          videoElement.style.border = 'none'
        } catch {
          // Play was prevented - will try again
        }
      }

      // Try to play immediately
      attemptPlay()

      // Try when video is ready to play
      const tryPlayWhenReady = () => {
        if (videoElement.readyState >= 2) {
          attemptPlay()
        }
      }

      // Multiple event listeners to catch when video is ready
      videoElement.addEventListener('loadedmetadata', tryPlayWhenReady, { once: true })
      videoElement.addEventListener('loadeddata', tryPlayWhenReady, { once: true })
      videoElement.addEventListener('canplay', tryPlayWhenReady, { once: true })
      videoElement.addEventListener('canplaythrough', attemptPlay, { once: true })

      // On any user interaction (scroll, touch, click), try to play
      const interactionEvents = ['touchstart', 'touchend', 'click', 'scroll', 'pointerdown']
      const handleInteraction = () => {
        attemptPlay()
        // Remove listeners after first interaction
        for (const event of interactionEvents) {
          document.removeEventListener(event, handleInteraction, { capture: true })
        }
      }

      for (const event of interactionEvents) {
        document.addEventListener(event, handleInteraction, { once: true, capture: true })
      }

      // Also try on visibility change (when user switches back to tab)
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          enforceMuted()
          attemptPlay()
        }
      }
      document.addEventListener('visibilitychange', handleVisibilityChange)

      // Store cleanup function for this video
      cleanupFunctions.push(() => {
        clearInterval(mutedInterval)
        videoElement.removeEventListener('volumechange', enforceMuted)
        document.removeEventListener('visibilitychange', handleVisibilityChange)
      })
    }

    // Return cleanup function for all videos
    return () => {
      for (const cleanup of cleanupFunctions) {
        cleanup()
      }
    }
  }, [])

  /**
   * Header scroll handler - changes header appearance when scrolled past hero
   */
  useEffect(() => {
    const handleScroll = () => {
      const heroHeight = 600
      setIsScrolled(window.scrollY > heroHeight)
    }

    window.addEventListener('scroll', handleScroll)
    handleScroll() // Check initial scroll position

    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  /**
   * Handle hash navigation on page load (e.g., from Terms/Privacy pages)
   */
  useEffect(() => {
    const handleHashNavigation = () => {
      const hash = window.location.hash
      if (hash) {
        const sectionId = hash.substring(1) // Remove the '#'
        const element = document.getElementById(sectionId)
        if (element) {
          // Wait for page to be fully rendered
          setTimeout(() => {
            const headerOffset = 80
            const elementPosition = element.getBoundingClientRect().top
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth',
            })
          }, 100)
        }
      }
    }

    // Handle hash on initial load
    handleHashNavigation()
  }, [])

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <MarketingNav variant="landing" isScrolled={isScrolled} onScrollToSection={scrollToSection} />
      <WhitepaperLeadModal open={leadModalOpen} onOpenChange={setLeadModalOpen} />

      {/* Hero Section */}
      <section className="pt-24 md:pt-32 relative overflow-hidden min-h-[720px] flex items-center">
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="w-full h-full object-cover"
          >
            <source src="/assets/Pelgo-Hero%202.mp4" type="video/mp4" />
            {/* Fallback image if video fails to load */}
            <img
              src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=1920&q=80"
              alt="Professionals in office meeting"
              className="w-full h-full object-cover"
            />
          </video>
        </div>
        <div className="container mx-auto px-4 md:px-16 py-16 md:py-32 relative z-20">
          <div className="max-w-4xl scroll-fade-up in-view">
            <h1 className="text-5xl md:text-[80px] font-display text-white leading-none mb-4 md:mb-6 tracking-[-1.6px]">
              Work changes.
              <br />
              People progress.
            </h1>
            <p className="text-lg md:text-2xl font-medium text-white/90 mb-8 md:mb-12 leading-relaxed max-w-3xl tracking-[-0.48px]">
              More than just outplacement. Personalized pathing for everyone.
            </p>
            <Button
              asChild
              className="border-2 border-white text-white rounded-full px-6 py-3 text-sm font-semibold bg-transparent hover:bg-white/10 transition-colors min-h-[48px]"
            >
              <Link to="/contact">Request Demo</Link>
            </Button>
          </div>
        </div>
      </section>

      <LogoMarquee />

      {/* Big Quote Section */}
      <section
        id="what-is-pelgo"
        className="py-16 md:py-32 scroll-fade-up"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)',
        }}
      >
        <div className="container mx-auto px-4 md:px-16">
          <div className="max-w-[1280px] mx-auto flex flex-col gap-8 items-start">
            {/* Eyebrow */}
            <div className="flex flex-col gap-8">
              <div className="h-3 w-12 bg-pelgo-blue-light rounded-full" />
              <p className="text-xs font-medium text-[#0a0a0a] tracking-[0.24px] uppercase">
                CAREER PATHWAYS ARE CHANGING
              </p>
            </div>
            <p className="text-3xl md:text-[48px] font-display text-[#0a0a0a] leading-[1.2] tracking-[-0.96px]">
              Everyone, in every industry or university, deserves access to real career pathing and
              skills support.
            </p>
            <Button
              asChild
              className="bg-pelgo-blue-light hover:bg-pelgo-blue-light/90 text-white rounded-full px-6 py-3 text-sm font-semibold min-h-[48px]"
            >
              <Link to="/contact">Request Demo</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Why Pelgo / What is Pelgo */}
      <section
        id="what-is-pelgo"
        className="py-16 md:py-32 scroll-fade-up"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)',
        }}
      >
        <div className="container mx-auto px-4 md:px-16">
          <div className="max-w-[1280px] mx-auto">
            {/* Section Header */}
            <div className="mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
              <div className="flex-1">
                <div className="flex flex-col gap-8 mb-4">
                  <div className="h-3 w-12 bg-pelgo-blue-light rounded-full" />
                  <p className="text-xs font-medium text-gray-900 tracking-[0.24px] uppercase">
                    WHAT IS PELGO
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <h2 className="text-4xl md:text-[48px] font-display text-gray-900 leading-[1.2] tracking-[-0.96px]">
                    Pelgo works for you.
                  </h2>
                  <p className="text-4xl md:text-[48px] font-display text-gray-400 leading-[1.2] tracking-[-0.96px]">
                    Whether it's embarking on a new career, re-skilling, starting a new business or
                    navigating retirement, Pelgo guides team members through their toughest or
                    biggest career moments.
                  </p>
                </div>
              </div>
            </div>

            {/* 3 Column Content */}
            <div className="grid md:grid-cols-3 gap-8 md:gap-16">
              {/* Column 1 */}
              <div className="flex flex-col gap-8">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <h3 className="text-lg font-medium text-gray-900 tracking-[-0.36px]">
                      Transitions tailored to real career paths
                    </h3>
                    <p className="text-base font-medium text-gray-500 tracking-[-0.32px]">
                      Bridge the gap with the most convenient, easy-to-use platform, making
                      context-aware progress every step of the way.
                    </p>
                  </div>
                </div>
                <div className="bg-blue-100 rounded-2xl overflow-hidden aspect-[4/3]">
                  <img
                    src="/assets/transitions-tailored.png"
                    alt="Career transition"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Column 2 */}
              <div className="flex flex-col gap-8">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <h3 className="text-lg font-medium text-gray-900 tracking-[-0.36px]">
                      Compassion built into every step
                    </h3>
                    <p className="text-base font-medium text-gray-500 tracking-[-0.32px]">
                      From the notification moment to their next offer letter, our experience is
                      designed to reduce anxiety and build confidence through a personalized
                      pathway.
                    </p>
                  </div>
                </div>
                <div className="bg-blue-100 rounded-2xl overflow-hidden aspect-[4/3]">
                  <img
                    src="/assets/compassion-built.png"
                    alt="Compassionate support"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Column 3 */}
              <div className="flex flex-col gap-8">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <h3 className="text-lg font-medium text-gray-900 tracking-[-0.36px]">
                      Real support. Real people.
                    </h3>
                    <p className="text-base font-medium text-gray-500 tracking-[-0.32px]">
                      Technology drives the process, but humans drive the connection. Every user can
                      access certified coaching for resume review and interview prep.
                    </p>
                  </div>
                </div>
                <div className="bg-blue-100 rounded-2xl overflow-hidden aspect-[4/3]">
                  <img
                    src="/assets/real-support-people.png"
                    alt="Real people support"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section
        id="how-it-works"
        className="py-16 md:py-32 scroll-fade-up"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)',
        }}
      >
        <div className="container mx-auto px-4 md:px-16">
          <div className="max-w-[1280px] mx-auto">
            {/* Section Header */}
            <div className="mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
              <div className="flex-1">
                <div className="flex flex-col gap-8 mb-4">
                  <div className="h-3 w-12 bg-pelgo-blue-light rounded-full" />
                  <p className="text-xs font-medium text-gray-900 tracking-[0.24px] uppercase">
                    HOW IT WORKS
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <h2 className="text-4xl md:text-[48px] font-display text-gray-900 leading-[1.2] tracking-[-0.96px]">
                    Offboard in a modern, scalable way.
                  </h2>
                  <p className="text-4xl md:text-[48px] font-display text-gray-400 leading-[1.2] tracking-[-0.96px]">
                    Replace the cold 'exit packet' with a modern experience that empowers people to
                    start their next chapter.
                  </p>
                </div>
              </div>
            </div>

            {/* Content: Left Text List + Right Image */}
            <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-start">
              {/* Left: Text List */}
              <div className="flex flex-col gap-8">
                <div className="flex flex-col gap-2">
                  <h3 className="text-lg font-medium text-gray-900 tracking-[-0.36px]">
                    Dignified Exit Experiences
                  </h3>
                  <p className="text-base font-medium text-gray-500 tracking-[-0.32px]">
                    A guided experience that treats people with dignity while keeping your process
                    clear.
                  </p>
                </div>

                <div className="flex flex-col gap-2">
                  <h3 className="text-lg font-medium text-gray-900 tracking-[-0.36px]">
                    Career Path Discovery
                  </h3>
                  <p className="text-base font-medium text-gray-500 tracking-[-0.32px]">
                    Skill insights and market data that highlight realistic jobs people can move
                    into.
                  </p>
                </div>

                <div className="flex flex-col gap-2">
                  <h3 className="text-lg font-medium text-gray-900 tracking-[-0.36px]">
                    Market-Aligned Upskilling
                  </h3>
                  <p className="text-base font-medium text-gray-500 tracking-[-0.32px]">
                    Learning paths built around each person's goals, pace, and existing abilities.
                  </p>
                </div>

                <div className="flex flex-col gap-2">
                  <h3 className="text-lg font-medium text-gray-900 tracking-[-0.36px]">
                    Placement & Alumni Networking
                  </h3>
                  <p className="text-base font-medium text-gray-500 tracking-[-0.32px]">
                    Coaching, job search tools, and resources that continue until they land a new
                    role.
                  </p>
                </div>
              </div>

              {/* Right: Image */}
              <div className="bg-blue-50 rounded-2xl overflow-hidden aspect-[624/480] relative">
                <img
                  src="/assets/how-it-works.png"
                  alt="How it works"
                  className="w-full h-full object-cover object-center"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Section */}
      <section
        id="platform"
        className="py-16 md:py-32 scroll-fade-up"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)',
        }}
      >
        <div className="container mx-auto px-4 md:px-16">
          <div className="max-w-[1280px] mx-auto">
            {/* Section Header */}
            <div className="mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
              <div className="flex-1">
                <div className="flex flex-col gap-8 mb-4">
                  <div className="h-3 w-12 bg-pelgo-blue-light rounded-full" />
                  <p className="text-xs font-medium text-gray-900 tracking-[0.24px] uppercase">
                    PLATFORM
                  </p>
                </div>
                <div className="flex flex-col gap-1">
                  <h2 className="text-4xl md:text-[48px] font-display text-gray-900 leading-[1.2] tracking-[-0.96px]">
                    The support they deserve.
                  </h2>
                  <p className="text-4xl md:text-[48px] font-display text-gray-400 leading-[1.2] tracking-[-0.96px]">
                    Our platform combines AI-driven job services with a centralized command center
                    for People teams to track placement success.
                  </p>
                </div>
              </div>
            </div>

            {/* Screenshot */}
            <div className="bg-transparent md:bg-[#d1e0ff] rounded-none md:rounded-[32px] p-0 md:p-16 mb-16 overflow-visible md:overflow-hidden flex flex-col gap-2 items-center">
              <div className="aspect-[1920/1080] relative rounded-none md:rounded-[8px] w-full shrink-0 overflow-hidden">
                <img
                  src="/assets/32d5db25d2d7ec6976efea5720e62468d94f1ac6.png"
                  alt="Platform dashboard"
                  className="absolute inset-0 max-w-none object-cover object-[50%_50%] pointer-events-none rounded-none md:rounded-[8px] w-full h-full"
                />
              </div>
            </div>

            {/* Feature Grid - 3x3 */}
            <div className="grid md:grid-cols-3 gap-8 md:gap-12">
              {/* Row 1 */}
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <h3 className="text-lg font-medium text-gray-900 tracking-[-0.36px]">
                    Smart Resume Builder
                  </h3>
                  <p className="text-base font-medium text-gray-500 tracking-[-0.32px]">
                    AI-powered tools that auto-format and keyword-optimize resumes for Applicant
                    Tracking Systems (ATS).
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <h3 className="text-lg font-medium text-gray-900 tracking-[-0.36px]">
                    Job Market Heatmaps
                  </h3>
                  <p className="text-base font-medium text-gray-500 tracking-[-0.32px]">
                    Real-time data showing employees which industries are hiring for their specific
                    skill sets.
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <h3 className="text-lg font-medium text-gray-900 tracking-[-0.36px]">
                    1:1 Coaching Scheduler
                  </h3>
                  <p className="text-base font-medium text-gray-500 tracking-[-0.32px]">
                    Seamless booking integration for employees to meet with career counselors and
                    mental health support.
                  </p>
                </div>
              </div>

              {/* Row 2 */}
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <h3 className="text-lg font-medium text-gray-900 tracking-[-0.36px]">
                    Sentiment Tracking
                  </h3>
                  <p className="text-base font-medium text-gray-500 tracking-[-0.32px]">
                    Monitor the morale of the cohort to identify individuals who may need additional
                    human intervention.
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <h3 className="text-lg font-medium text-gray-900 tracking-[-0.36px]">
                    Placement Analytics
                  </h3>
                  <p className="text-base font-medium text-gray-500 tracking-[-0.32px]">
                    Executive dashboards showing how many employees have landed interviews or new
                    roles.
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <h3 className="text-lg font-medium text-gray-900 tracking-[-0.36px]">
                    LinkedIn Integration
                  </h3>
                  <p className="text-base font-medium text-gray-500 tracking-[-0.32px]">
                    One-click profile optimization tools and networking templates to boost
                    visibility.
                  </p>
                </div>
              </div>

              {/* Row 3 */}
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <h3 className="text-lg font-medium text-gray-900 tracking-[-0.36px]">
                    Cohort Management
                  </h3>
                  <p className="text-base font-medium text-gray-500 tracking-[-0.32px]">
                    Easily segment departing teams by department or location to provide tailored
                    resources.
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <h3 className="text-lg font-medium text-gray-900 tracking-[-0.36px]">
                    Upskilling Library
                  </h3>
                  <p className="text-base font-medium text-gray-500 tracking-[-0.32px]">
                    Access to thousands of courses mapped to future job goals.
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <h3 className="text-lg font-medium text-gray-900 tracking-[-0.36px]">
                    Alumni Community
                  </h3>
                  <p className="text-base font-medium text-gray-500 tracking-[-0.32px]">
                    A branded portal where former employees can network, share leads, and stay
                    connected to your brand.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Andrew Ng Quote */}
      <section className="hidden py-16 md:py-32 bg-white scroll-fade-up">
        <div className="container mx-auto px-4 md:px-8">
          <div
            className="max-w-[1280px] mx-auto bg-white rounded-[32px] p-6 md:p-16"
            style={{
              backgroundImage:
                'linear-gradient(rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)',
            }}
          >
            <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-start">
              <div className="w-full h-[400px] overflow-hidden rounded-2xl relative">
                <img
                  src="/assets/e9faff75b7bcf598bb4589fbf766e632551ae453.png"
                  alt="Andrew Ng"
                  className="w-full h-full object-cover object-[50%_30%]"
                />
              </div>
              <div className="flex flex-col gap-6 md:gap-8 py-6 md:py-16 items-center justify-center">
                <blockquote className="text-2xl md:text-[32px] font-display text-gray-900 leading-none tracking-[-0.64px]">
                  "Artificial intelligence is the new electricity. Just like we adapted to
                  electricity, we must adapt to AI.{' '}
                  <span className="font-display text-[#155dfc]">
                    Those who learn to use it will thrive.
                  </span>
                  "
                </blockquote>
                <div className="flex flex-col gap-1 w-full">
                  <p className="text-lg font-medium text-gray-900 tracking-[-0.36px]">Andrew Ng</p>
                  <p className="text-sm font-medium text-gray-500 tracking-[-0.28px]">
                    AI pioneer, former Google Brain lead
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Insights Section */}
      <section
        id="insights"
        className="py-16 md:py-32 scroll-fade-up"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)',
        }}
      >
        <div className="container mx-auto px-4 md:px-16">
          <div className="max-w-[1280px] mx-auto">
            {/* Section Header */}
            <div className="mb-16 flex flex-col gap-8">
              {/* Eyebrow */}
              <div className="flex flex-col gap-8">
                <div className="h-3 w-12 bg-pelgo-blue-light rounded-full" />
                <p className="text-xs font-medium text-[#0a0a0a] tracking-[0.24px] uppercase">
                  INSIGHTS
                </p>
              </div>

              {/* Heading and supporting text */}
              <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
                <div className="flex-1">
                  <div className="flex flex-col gap-1">
                    <h2 className="text-4xl md:text-[48px] font-display text-[#0a0a0a] leading-[1.2] tracking-[-0.96px]">
                      Navigate critical transitions with confidence.
                    </h2>
                    <p className="text-4xl md:text-[48px] font-display text-[#a1a1a1] leading-[1.2] tracking-[-0.96px]">
                      Support your people through every pivot with infrastructure for workforce
                      mobility.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-4 mb-16">
              {useCaseTabs.map((tab, index) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveUseCase(index)}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-colors min-h-[37px] ${
                    activeUseCase === index
                      ? 'border border-[#0a0a0a] text-[#0a0a0a] bg-transparent'
                      : 'bg-[#eee] text-[#a1a1a1] hover:bg-gray-200'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Content */}
            <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-start">
              {/* Left: Text Content */}
              <div className="flex flex-col gap-8">
                <div className="flex flex-col gap-4">
                  <h3 className="text-lg font-medium text-[#0a0a0a] tracking-[-0.36px]">
                    {useCaseContent[activeUseCase]?.title}
                  </h3>
                  <p className="text-base font-medium text-[#858585] tracking-[-0.32px]">
                    {useCaseContent[activeUseCase]?.description}
                  </p>
                </div>
              </div>

              {/* Right: Image */}
              <div className="relative rounded-2xl overflow-hidden aspect-[608/342]">
                <img
                  src={useCaseContent[activeUseCase]?.imageSrc}
                  alt={useCaseContent[activeUseCase]?.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section
        id="faq"
        className="py-16 md:py-32 scroll-fade-up"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)',
        }}
      >
        <div className="container mx-auto px-4 md:px-16">
          <div className="max-w-[1280px] mx-auto">
            {/* Section Header */}
            <div className="mb-16">
              <div className="flex flex-col gap-8 mb-4">
                <div className="h-3 w-12 bg-pelgo-blue-light rounded-full" />
                <p className="text-xs font-medium text-gray-900 tracking-[0.24px] uppercase">
                  FREQUENTLY ASKED QUESTIONS
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-8 md:gap-16 items-start">
                <div>
                  <div className="flex flex-col gap-1 mb-6">
                    <h2 className="text-4xl md:text-[48px] font-display text-gray-900 leading-[1.2] tracking-[-0.96px]">
                      Questions?
                    </h2>
                    <p className="text-4xl md:text-[48px] font-display text-gray-400 leading-[1.2] tracking-[-0.96px]">
                      We have answers.
                    </p>
                  </div>
                  <p className="text-base font-medium text-gray-500 tracking-[-0.32px] mb-8">
                    We guide employees through one of the toughest moments in their careers and give
                    them a real chance at a fresh start. Our tools combine structured support with
                    personalized coaching and job-ready training.
                  </p>
                </div>

                {/* FAQ List */}
                <div className="flex flex-col gap-8">
                  {/* General Section */}
                  <div className="flex flex-col gap-4">
                    <h3 className="text-xl font-medium text-gray-900 tracking-[-0.4px]">General</h3>
                    <div
                      className="bg-white rounded-lg overflow-hidden"
                      style={{ boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setOpenFaqItems((prev) => {
                            const next = new Set(prev)
                            if (next.has('faq-general-1')) {
                              next.delete('faq-general-1')
                            } else {
                              next.add('faq-general-1')
                            }
                            return next
                          })
                        }}
                        className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                      >
                        <h4 className="text-base font-medium text-gray-900 tracking-[-0.32px] pr-4">
                          How quickly can we implement Pelgo?
                        </h4>
                        <ChevronDown
                          className={`h-5 w-5 text-gray-500 flex-shrink-0 transition-transform ${
                            openFaqItems.has('faq-general-1') ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      {openFaqItems.has('faq-general-1') && (
                        <div className="px-6 pb-6">
                          <p className="text-sm font-medium text-gray-500 tracking-[-0.28px] leading-relaxed">
                            We know time is sensitive. Pelgo can be deployed in as little as 48
                            hours, allowing you to upload employee lists and invite users
                            immediately following notification.
                          </p>
                        </div>
                      )}
                    </div>
                    <div
                      className="bg-white rounded-lg overflow-hidden"
                      style={{ boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setOpenFaqItems((prev) => {
                            const next = new Set(prev)
                            if (next.has('faq-general-2')) {
                              next.delete('faq-general-2')
                            } else {
                              next.add('faq-general-2')
                            }
                            return next
                          })
                        }}
                        className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                      >
                        <h4 className="text-base font-medium text-gray-900 tracking-[-0.32px] pr-4">
                          Is Pelgo suitable for remote or global workforces?
                        </h4>
                        <ChevronDown
                          className={`h-5 w-5 text-gray-500 flex-shrink-0 transition-transform ${
                            openFaqItems.has('faq-general-2') ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      {openFaqItems.has('faq-general-2') && (
                        <div className="px-6 pb-6">
                          <p className="text-sm font-medium text-gray-500 tracking-[-0.28px] leading-relaxed">
                            Yes. Our platform is cloud-based and supports 10+ languages, ensuring a
                            consistent offboarding experience regardless of location.
                          </p>
                        </div>
                      )}
                    </div>
                    <div
                      className="bg-white rounded-lg overflow-hidden"
                      style={{ boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setOpenFaqItems((prev) => {
                            const next = new Set(prev)
                            if (next.has('faq-general-3')) {
                              next.delete('faq-general-3')
                            } else {
                              next.add('faq-general-3')
                            }
                            return next
                          })
                        }}
                        className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                      >
                        <h4 className="text-base font-medium text-gray-900 tracking-[-0.32px] pr-4">
                          How does this protect our employer brand?
                        </h4>
                        <ChevronDown
                          className={`h-5 w-5 text-gray-500 flex-shrink-0 transition-transform ${
                            openFaqItems.has('faq-general-3') ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      {openFaqItems.has('faq-general-3') && (
                        <div className="px-6 pb-6">
                          <p className="text-sm font-medium text-gray-500 tracking-[-0.28px] leading-relaxed">
                            By investing in the future of your departing staff, you reduce negative
                            reviews (Glassdoor) and maintain trust with remaining employees who see
                            their colleagues treated well.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Process & Engagement Section */}
                  <div className="flex flex-col gap-4">
                    <h3 className="text-xl font-medium text-gray-900 tracking-[-0.4px]">
                      Process & engagement
                    </h3>
                    <div
                      className="bg-white rounded-lg overflow-hidden"
                      style={{ boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setOpenFaqItems((prev) => {
                            const next = new Set(prev)
                            if (next.has('faq-process-1')) {
                              next.delete('faq-process-1')
                            } else {
                              next.add('faq-process-1')
                            }
                            return next
                          })
                        }}
                        className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                      >
                        <h4 className="text-base font-medium text-gray-900 tracking-[-0.32px] pr-4">
                          What happens after an employee is uploaded?
                        </h4>
                        <ChevronDown
                          className={`h-5 w-5 text-gray-500 flex-shrink-0 transition-transform ${
                            openFaqItems.has('faq-process-1') ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      {openFaqItems.has('faq-process-1') && (
                        <div className="px-6 pb-6">
                          <p className="text-sm font-medium text-gray-500 tracking-[-0.28px] leading-relaxed">
                            They receive a branded invitation email to access the portal, where they
                            immediately begin their intake assessment and meet their dedicated
                            career coach.
                          </p>
                        </div>
                      )}
                    </div>
                    <div
                      className="bg-white rounded-lg overflow-hidden"
                      style={{ boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setOpenFaqItems((prev) => {
                            const next = new Set(prev)
                            if (next.has('faq-process-2')) {
                              next.delete('faq-process-2')
                            } else {
                              next.add('faq-process-2')
                            }
                            return next
                          })
                        }}
                        className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                      >
                        <h4 className="text-base font-medium text-gray-900 tracking-[-0.32px] pr-4">
                          How long do employees have access to the platform?
                        </h4>
                        <ChevronDown
                          className={`h-5 w-5 text-gray-500 flex-shrink-0 transition-transform ${
                            openFaqItems.has('faq-process-2') ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      {openFaqItems.has('faq-process-2') && (
                        <div className="px-6 pb-6">
                          <p className="text-sm font-medium text-gray-500 tracking-[-0.28px] leading-relaxed">
                            Access typically lasts for 3 to 6 months depending on your plan, or
                            until the employee secures a new role.
                          </p>
                        </div>
                      )}
                    </div>
                    <div
                      className="bg-white rounded-lg overflow-hidden"
                      style={{ boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setOpenFaqItems((prev) => {
                            const next = new Set(prev)
                            if (next.has('faq-process-3')) {
                              next.delete('faq-process-3')
                            } else {
                              next.add('faq-process-3')
                            }
                            return next
                          })
                        }}
                        className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                      >
                        <h4 className="text-base font-medium text-gray-900 tracking-[-0.32px] pr-4">
                          Can we track the success of the outplacement?
                        </h4>
                        <ChevronDown
                          className={`h-5 w-5 text-gray-500 flex-shrink-0 transition-transform ${
                            openFaqItems.has('faq-process-3') ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      {openFaqItems.has('faq-process-3') && (
                        <div className="px-6 pb-6">
                          <p className="text-sm font-medium text-gray-500 tracking-[-0.28px] leading-relaxed">
                            Absolutely. Your HR dashboard provides real-time analytics on engagement
                            rates, skills acquired, and placement success.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Pricing & Fees Section */}
                  <div className="flex flex-col gap-4">
                    <h3 className="text-xl font-medium text-gray-900 tracking-[-0.4px]">
                      Pricing & fees
                    </h3>
                    <div
                      className="bg-white rounded-lg overflow-hidden"
                      style={{ boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setOpenFaqItems((prev) => {
                            const next = new Set(prev)
                            if (next.has('faq-pricing-1')) {
                              next.delete('faq-pricing-1')
                            } else {
                              next.add('faq-pricing-1')
                            }
                            return next
                          })
                        }}
                        className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                      >
                        <h4 className="text-base font-medium text-gray-900 tracking-[-0.32px] pr-4">
                          Is pricing per-seat or a flat enterprise fee?
                        </h4>
                        <ChevronDown
                          className={`h-5 w-5 text-gray-500 flex-shrink-0 transition-transform ${
                            openFaqItems.has('faq-pricing-1') ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      {openFaqItems.has('faq-pricing-1') && (
                        <div className="px-6 pb-6">
                          <p className="text-sm font-medium text-gray-500 tracking-[-0.28px] leading-relaxed">
                            We offer a flexible per-seat model for smaller cohorts and volume
                            licensing for large-scale restructuring.
                          </p>
                        </div>
                      )}
                    </div>
                    <div
                      className="bg-white rounded-lg overflow-hidden"
                      style={{ boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setOpenFaqItems((prev) => {
                            const next = new Set(prev)
                            if (next.has('faq-pricing-2')) {
                              next.delete('faq-pricing-2')
                            } else {
                              next.add('faq-pricing-2')
                            }
                            return next
                          })
                        }}
                        className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                      >
                        <h4 className="text-base font-medium text-gray-900 tracking-[-0.32px] pr-4">
                          Are coaching hours included in the base price?
                        </h4>
                        <ChevronDown
                          className={`h-5 w-5 text-gray-500 flex-shrink-0 transition-transform ${
                            openFaqItems.has('faq-pricing-2') ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      {openFaqItems.has('faq-pricing-2') && (
                        <div className="px-6 pb-6">
                          <p className="text-sm font-medium text-gray-500 tracking-[-0.28px] leading-relaxed">
                            Yes, every license includes a set number of 1:1 coaching hours, with the
                            option for employees to purchase more if needed.
                          </p>
                        </div>
                      )}
                    </div>
                    <div
                      className="bg-white rounded-lg overflow-hidden"
                      style={{ boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)' }}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setOpenFaqItems((prev) => {
                            const next = new Set(prev)
                            if (next.has('faq-pricing-3')) {
                              next.delete('faq-pricing-3')
                            } else {
                              next.add('faq-pricing-3')
                            }
                            return next
                          })
                        }}
                        className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 transition-colors"
                      >
                        <h4 className="text-base font-medium text-gray-900 tracking-[-0.32px] pr-4">
                          Is there a setup or implementation fee?
                        </h4>
                        <ChevronDown
                          className={`h-5 w-5 text-gray-500 flex-shrink-0 transition-transform ${
                            openFaqItems.has('faq-pricing-3') ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      {openFaqItems.has('faq-pricing-3') && (
                        <div className="px-6 pb-6">
                          <p className="text-sm font-medium text-gray-500 tracking-[-0.28px] leading-relaxed">
                            For standard deployments, setup is included. Custom integrations with
                            your HRIS may incur a one-time fee.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section
        id="pricing"
        className="py-16 md:py-32 scroll-fade-up"
        style={{
          backgroundImage:
            'linear-gradient(rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.04) 100%), linear-gradient(90deg, rgb(255, 255, 255) 0%, rgb(255, 255, 255) 100%)',
        }}
      >
        <div className="container mx-auto px-4 md:px-16">
          <div className="max-w-[1280px] mx-auto">
            <div className="flex flex-col gap-8 md:gap-16 items-start">
              {/* Eyebrow */}
              <div className="flex flex-col gap-8">
                <div className="h-3 w-12 bg-pelgo-blue-light rounded-full" />
                <p className="text-xs font-medium text-gray-900 tracking-[0.24px] uppercase">
                  PRICING
                </p>
              </div>
              {/* Heading and Supporting Text */}
              <div className="flex flex-col gap-4 md:gap-8 items-start">
                <div className="flex flex-col gap-1">
                  <h2 className="text-4xl md:text-[48px] font-display text-gray-900 leading-[1.2] tracking-[-0.96px]">
                    Our mission: to provide every departure in every industry with effective
                    outplacement support.
                  </h2>
                  <p className="text-4xl md:text-[48px] font-display text-gray-400 leading-[1.2] tracking-[-0.96px]">
                    We do so by providing a powerful suite of tools at a fraction of what
                    traditional outplacement services cost.
                  </p>
                </div>
                <Button
                  asChild
                  className="bg-pelgo-blue-light hover:bg-pelgo-blue-light/90 text-white rounded-full px-6 py-3 text-base font-semibold min-h-[48px]"
                >
                  <Link to="/contact">Request Demo</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Request Demo Video Section */}
      <section className="relative bg-white scroll-fade-up">
        <div className="relative w-full px-6 md:px-24 py-12 md:py-32 overflow-hidden flex items-center justify-center min-h-[360px] md:min-h-[500px]">
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="absolute inset-0 max-w-none object-cover w-full h-full"
            controlsList="nodownload"
          >
            <source
              src="https://videos.pexels.com/video-files/853870/853870-hd_1920_1080_25fps.mp4"
              type="video/mp4"
            />
            {/* Fallback image if video fails to load */}
            <img
              src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=1920&q=80"
              alt="Blurry video of people"
              className="absolute inset-0 max-w-none object-cover w-full h-full"
            />
          </video>
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-black/70" />
          <div className="relative flex flex-col gap-4 md:gap-8 items-center text-center max-w-[1280px] w-full">
            <div className="flex flex-col gap-4 items-center text-white">
              <h2 className="text-3xl md:text-[64px] font-display text-white leading-none tracking-[-0.6px] md:tracking-[-1.28px]">
                We provide every departure in every industry with effective outplacement support.
              </h2>
              <p className="text-base md:text-xl font-normal text-white leading-[1.5] max-w-[768px] tracking-[-0.32px] md:tracking-[-0.4px]">
                We do so by providing a powerful suite of tools at a fraction of what traditional
                outplacement services cost.
              </p>
            </div>
            <Button
              asChild
              className="bg-pelgo-blue-light hover:bg-pelgo-blue-light/90 text-white rounded-full px-6 py-3 text-base font-semibold min-h-[48px]"
            >
              <Link to="/contact">Request Demo</Link>
            </Button>
          </div>
        </div>
      </section>

      <SiteFooter className="bg-white w-full scroll-fade-up p-4 md:p-8" />
    </div>
  )
}
